import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { CenterService } from '../../../core/services/center.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { AppDatePipe } from '../../../core/pipes/app-date.pipe';

// Browser BarcodeDetector (Chrome/Edge 88+)
declare const BarcodeDetector: any;

@Component({
  selector: 'app-issue-receive',
  standalone: true,
  imports: [CommonModule, FormsModule, AppDatePipe],
  templateUrl: './issue-receive.component.html',
  styleUrl: './issue-receive.component.scss',
})
export class IssueReceiveComponent implements OnInit, OnDestroy {

  @ViewChild('scanVideo')    scanVideoRef!:  ElementRef<HTMLVideoElement>;
  @ViewChild('bookSrNoField') bookSrNoField!: ElementRef<HTMLInputElement>;

  // ── Member selection ──────────────────────────────────────────────────────
  searchNo    = '';
  searchName  = '';
  searchPhone = '';
  memberResults: any[] = [];
  isMembersSearching = false;
  isMemberLookup     = false;
  selectedMemberId   = '';
  hasSearched  = false;
  searchedFor  = '';

  // ── Loaded member ─────────────────────────────────────────────────────────
  member: any = null;
  maxBooks = 5;

  // ── Transaction date ──────────────────────────────────────────────────────
  transactionDate = '';
  todayStr = '';

  // ── Receive ───────────────────────────────────────────────────────────────
  selectedForReturn = new Set<number>();
  selectAll = false;
  returnError = '';

  // ── Issue ─────────────────────────────────────────────────────────────────
  bookSrNoInput = '';
  issuedBooks: any[] = [];
  isValidating = false;
  addError = '';

  // ── Camera scan ───────────────────────────────────────────────────────────
  showScanOverlay = false;
  scanSupported = false;
  scanStatus = 'Point camera at barcode...';
  private scanStream: MediaStream | null = null;
  private scanActive = false;
  private scanFrameId = 0;

  // ── Renew membership ──────────────────────────────────────────────────────
  showRenewModal = false;
  renewMonths = 12;
  isRenewing = false;

  // ── Allocated claims (reserved books) ────────────────────────────────────
  allocatedClaims: any[] = [];
  isLoadingClaims = false;

  // ── Reserved books modal ──────────────────────────────────────────────────
  showReservedModal = false;

  // ── Submit ────────────────────────────────────────────────────────────────
  isSubmitting = false;
  showConfirmModal = false;
  successResult: any = null;
  errorMessage = '';
  remarks = '';

  constructor(
    private api: ApiService,
    private centerSvc: CenterService,
    private pageTitle: PageTitleService,
  ) { }

  ngOnInit(): void {
    this.pageTitle.set('Issue / Receive Books', 'Process member book transactions at the center');
    this.scanSupported = 'BarcodeDetector' in window;
    const today = new Date();
    this.todayStr = today.toISOString().slice(0, 10);
    this.transactionDate = this.todayStr;
  }

  ngOnDestroy(): void { this.stopScan(); }

  // ── Member search (server-side on demand) ─────────────────────────────────

  searchByField(field: 'no' | 'name' | 'phone'): void {
    this.hasSearched = true;
    this.memberResults = [];

    let params: any = { isActive: 'true' };

    if (field === 'no') {
      const no = this.searchNo.trim();
      if (!no) return;
      this.searchedFor = `Member No: ${no}`;
      params = { memberNo: no, isActive: 'true' };
    } else if (field === 'name') {
      const name = this.searchName.trim();
      if (name.length < 3) return;
      this.searchedFor = `Name: "${name}"`;
      params = { search: name, isActive: 'true' };
    } else {
      const phone = this.searchPhone.trim();
      if (!phone) return;
      this.searchedFor = `Mobile: ${phone}`;
      params = { search: phone, isActive: 'true' };
    }

    this.isMembersSearching = true;
    this.api.getMembers(params).subscribe({
      next: (data) => {
        this.memberResults = data || [];
        this.isMembersSearching = false;
      },
      error: () => {
        this.memberResults = [];
        this.isMembersSearching = false;
      },
    });
  }

  selectMember(m: any): void {
    this.selectedMemberId = m._id;
    this.isMemberLookup = true;
    this.errorMessage = '';
    this.api.getMemberById(m._id).subscribe({
      next: (data) => { this.applyMember(data); this.isMemberLookup = false; },
      error: (err) => { this.errorMessage = err.message || 'Failed to load member.'; this.isMemberLookup = false; },
    });
  }

  private applyMember(data: any): void {
    this.member = {
      ...data,
      borrowedBooks: (data.borrowedBooks || []).map((b: any) => ({
        ...b,
        bookSrNo: Number(b.bookSrNo),
      })),
    };
    this.maxBooks = data.maxBooksAllowed || 5;
    this.resetTransaction();
    this.loadAllocatedClaims(data._id);
  }

  private loadAllocatedClaims(memberId: string): void {
    this.isLoadingClaims = true;
    this.allocatedClaims = [];
    this.api.getClaimsForMember(memberId, 'allocated').subscribe({
      next: (claims) => {
        this.allocatedClaims = claims || [];
        this.isLoadingClaims = false;
        // Reserved books are displayed separately — operator must explicitly click Issue
        // to add them to issuedBooks so they count toward the transaction.
      },
      error: () => { this.isLoadingClaims = false; },
    });
  }

  issueReservedBook(claim: any): void {
    if (this.isReservedBookAdded(claim)) return;
    if (this.getAvailableSlots() <= 0) {
      this.addError = `Book limit of ${this.maxBooks} reached. Return a book first.`;
      return;
    }
    this.addError = '';
    this.issuedBooks = [...this.issuedBooks, {
      bookSrNo:   claim.allocatedBookSrNo,
      bookId:     claim.bookId,
      bookName:   claim.bookName,
      author:     '',
      _fromClaim: true,
      claimId:    claim.claimId,
    }];
  }

  issueAllReserved(): void {
    this.addError = '';
    for (const claim of this.allocatedClaims) {
      if (this.getAvailableSlots() <= 0) {
        this.addError = `Book limit of ${this.maxBooks} reached — not all reserved books could be added.`;
        break;
      }
      this.issueReservedBook(claim);
    }
  }

  isReservedBookAdded(claim: any): boolean {
    return !!this.issuedBooks.find(b => Number(b.bookSrNo) === Number(claim.allocatedBookSrNo));
  }

  changeMember(): void {
    this.member = null;
    this.selectedMemberId = '';
    this.resetTransaction();
    // searchNo/Name/Phone, memberResults, hasSearched and searchedFor kept
    // so the previous list is still visible when returning to the search view.
  }

  private resetTransaction(): void {
    this.selectedForReturn.clear();
    this.selectAll = false;
    this.issuedBooks = [];
    this.allocatedClaims = [];
    this.bookSrNoInput = '';
    this.addError = '';
    this.returnError = '';
    this.errorMessage = '';
    this.remarks = '';
    this.transactionDate = this.todayStr;
  }

  // ── Receive helpers ───────────────────────────────────────────────────────

  getBorrowedBooks(): any[] { return this.member?.borrowedBooks || []; }

  private rebuildReturnError(): void {
    const slots = this.maxBooks - (this.getBorrowedBooks().length - this.selectedForReturn.size + this.issuedBooks.length);
    if (slots < 0) {
      const excess = -slots;
      this.returnError =
        `Cannot issue ${this.issuedBooks.length} book${this.issuedBooks.length !== 1 ? 's' : ''} — ` +
        `remove ${excess} book${excess !== 1 ? 's' : ''} from the issue list first.`;
    } else {
      this.returnError = '';
    }
  }

  toggleSelectAll(): void {
    if (this.selectAll) {
      this.selectedForReturn = new Set();
      this.selectAll = false;
    } else {
      this.addError = '';
      const next = new Set<number>();
      this.getBorrowedBooks().forEach((_, i) => next.add(i));
      this.selectedForReturn = next;
      this.selectAll = true;
    }
    this.rebuildReturnError();
  }

  toggleBookReturn(i: number): void {
    const next = new Set(this.selectedForReturn);
    if (next.has(i)) {
      next.delete(i);
    } else {
      this.addError = '';
      next.add(i);
    }
    this.selectedForReturn = next;
    this.selectAll = this.selectedForReturn.size === this.getBorrowedBooks().length;
    this.rebuildReturnError();
  }

  isSelected(i: number): boolean { return this.selectedForReturn.has(i); }

  getReturnBooks(): any[] {
    return this.getBorrowedBooks().filter((_, i) => this.selectedForReturn.has(i));
  }

  // ── Issue helpers ─────────────────────────────────────────────────────────

  getAvailableSlots(): number {
    return this.maxBooks - (this.getBorrowedBooks().length - this.selectedForReturn.size + this.issuedBooks.length);
  }

  getCurrentHolding(): number {
    return (this.getBorrowedBooks().length - this.selectedForReturn.size) + this.issuedBooks.length;
  }

  memberStatusBadge(m: any): { label: string; cls: string } {
    const st = (m?.status || '').toUpperCase();
    if (st === 'CLOSED') return { label: 'Closed',   cls: 'badge bg-danger-subtle text-danger' };
    if (st === 'ONHOLD') return { label: 'On Hold',  cls: 'badge bg-warning-subtle text-warning' };
    const exp = m?.membershipExpiresOn ? new Date(m.membershipExpiresOn) : null;
    if (!exp) return { label: 'Active', cls: 'badge bg-success-subtle text-success' };
    const days = Math.floor((exp.getTime() - Date.now()) / 86400000);
    if (days < 0)   return { label: 'Closed',   cls: 'badge bg-danger-subtle text-danger' };
    if (days <= 30) return { label: 'Expiring', cls: 'badge bg-warning-subtle text-warning' };
    return { label: 'Active', cls: 'badge bg-success-subtle text-success' };
  }

  get membershipStatus(): 'active' | 'expiring' | 'closed' | 'hold' {
    const st = (this.member?.status || '').toUpperCase();
    if (st === 'CLOSED') return 'closed';
    if (st === 'ONHOLD') return 'hold';
    if (this.member?.membershipExpiresOn) {
      const days = Math.floor((new Date(this.member.membershipExpiresOn).getTime() - Date.now()) / 86400000);
      if (days < 0)   return 'closed';
      if (days <= 30) return 'expiring';
    }
    return 'active';
  }

  get addedClaimsCount(): number {
    return this.issuedBooks.filter(b => b._fromClaim).length;
  }

  openReservedModal(): void  { this.showReservedModal = true; }
  closeReservedModal(): void { this.showReservedModal = false; }

  get isExpired(): boolean { return this.membershipStatus === 'closed' || this.membershipStatus === 'hold'; }

  get showTransactionUI(): boolean { return !this.isExpired || this.getBorrowedBooks().length > 0; }

  addBook(): void {
    const srNo = this.bookSrNoInput.trim();
    if (!srNo) return;
    if (this.issuedBooks.find(b => Number(b.bookSrNo) === Number(srNo))) {
      this.addError = 'This copy is already in the issue list.'; return;
    }
    if (this.getAvailableSlots() <= 0) {
      this.addError = `Cannot add more — member limit of ${this.maxBooks} books would be exceeded.`; return;
    }

    this.isValidating = true;
    this.addError = '';
    this.api.validateBookAvailability(srNo).subscribe({
      next: (res: any) => {
        this.issuedBooks = [...this.issuedBooks, {
          bookSrNo: res.bookSrNo,
          bookId: res.book.bookId,
          bookName: res.book.bookName,
          author: res.book.authorNames?.[0] || res.book.author || '',
        }];
        this.bookSrNoInput = '';
        this.isValidating = false;
        if (this.getAvailableSlots() > 0) {
          setTimeout(() => this.bookSrNoField?.nativeElement?.focus(), 0);
        }
      },
      error: (err) => { this.addError = err.message || 'Copy not found or unavailable.'; this.isValidating = false; },
    });
  }

  removeBook(srNo: any): void {
    this.issuedBooks = this.issuedBooks.filter(b => Number(b.bookSrNo) !== Number(srNo));
    this.addError = '';
    this.rebuildReturnError();
  }

  isClaimBook(srNo: any): boolean {
    return !!this.issuedBooks.find(b => Number(b.bookSrNo) === Number(srNo) && b._fromClaim);
  }

  // ── Camera scanning ───────────────────────────────────────────────────────

  async startScan(): Promise<void> {
    if (!this.scanSupported) {
      this.addError = 'Camera scanning requires Chrome or Edge browser. Enter the Sr No manually or use a USB scanner.';
      return;
    }
    if (this.getAvailableSlots() <= 0) {
      this.addError = `Book limit of ${this.maxBooks} reached.`; return;
    }

    this.showScanOverlay = true;
    this.scanStatus = 'Starting camera...';
    this.addError = '';

    try {
      this.scanStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });

      // Wait one tick for *ngIf to render the <video> element
      await new Promise(r => setTimeout(r, 50));
      const video = this.scanVideoRef?.nativeElement || document.getElementById('irScanVideo') as HTMLVideoElement;
      if (video) {
        video.srcObject = this.scanStream;
        await video.play();
      }

      const detector = new BarcodeDetector({ formats: ['code_128', 'code_39', 'qr_code'] });
      this.scanActive = true;
      this.scanStatus = 'Point camera at barcode...';

      const scan = async () => {
        if (!this.scanActive) return;
        try {
          const barcodes = await detector.detect(video);
          if (barcodes.length > 0) {
            const raw = barcodes[0].rawValue?.trim();
            if (raw) {
              this.bookSrNoInput = raw;
              this.stopScan();
              this.addBook();
              return;
            }
          }
        } catch { /* frame not ready */ }
        this.scanFrameId = requestAnimationFrame(scan);
      };

      requestAnimationFrame(scan);

    } catch (err: any) {
      this.addError = err?.name === 'NotAllowedError'
        ? 'Camera permission denied. Allow camera access in browser settings.'
        : 'Could not access camera. Enter the Sr No manually.';
      this.showScanOverlay = false;
      this.stopScan();
    }
  }

  stopScan(): void {
    this.scanActive = false;
    if (this.scanFrameId) cancelAnimationFrame(this.scanFrameId);
    if (this.scanStream) {
      this.scanStream.getTracks().forEach(t => t.stop());
      this.scanStream = null;
    }
    this.showScanOverlay = false;
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  openConfirmModal(): void {
    if (!this.member || (this.selectedForReturn.size === 0 && this.issuedBooks.length === 0)) return;
    this.showConfirmModal = true;
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }

  saveTransaction(): void {
    this.closeConfirmModal();
    this.isSubmitting = true;
    this.errorMessage = '';

    const receivedBooks = this.getReturnBooks().map(b => ({
      bookSrNo: b.bookSrNo, bookId: b.bookId, bookName: b.bookDetails?.bookName || b.bookName,
    }));
    const issuedBooks = this.issuedBooks.map(b => ({
      bookSrNo: b.bookSrNo, bookId: b.bookId, bookName: b.bookName,
    }));

    this.api.syncTransaction({
      memberId: this.member._id, receivedBooks, issuedBooks,
      centerId: this.centerSvc.selectedCenter?._id || '',
      timestamp: this.transactionDate
        ? new Date(this.transactionDate + 'T12:00:00').toISOString()
        : new Date().toISOString(),
      remarks: this.remarks.trim(),
    }).subscribe({
      next: (result: any) => {
        this.successResult = result;
        this.member = null;
        this.isSubmitting = false;
        this.selectedMemberId = '';
        this.resetTransaction();
      },
      error: (err) => { this.errorMessage = err.message || 'Transaction failed.'; this.isSubmitting = false; },
    });
  }

  startNew(): void { this.successResult = null; this.errorMessage = ''; }

  // ── Renew membership ──────────────────────────────────────────────────────

  openRenewModal(): void {
    this.showRenewModal = true;
  }

  closeRenewModal(): void {
    this.showRenewModal = false;
  }

  renewMembership(): void {
    this.isRenewing = true;
    this.api.renewMembership(this.member._id, this.renewMonths).subscribe({
      next: (res: any) => {
        this.member.membershipExpiresOn = res.membershipExpiresOn;
        this.member.status = 'MEMBER';
        this.closeRenewModal();
        this.isRenewing = false;
      },
      error: (err) => {
        this.errorMessage = err.message || 'Failed to renew membership.';
        this.isRenewing = false;
        this.closeRenewModal();
      },
    });
  }
}
